import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type { Theme } from '@mui/material/styles';

// Soft shadow from the pinned column onto the cells scrolling under it. --DataGrid-hasScrollX is 1 only
// while the columns overflow, so the shadow disappears when nothing sits beneath the column (A-2109).
const PINNED_EDGE_SHADOW = '-12px 0 16px -8px rgba(0, 0, 0, calc(0.64 * var(--DataGrid-hasScrollX, 0)))';

export const MuiDataGrid = {
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        borderRadius: theme.shape.borderRadius,
        '--DataGrid-containerBackground': theme.vars!.palette.background['paper-elevation-1'],
        '--DataGrid-pinnedBackground': theme.vars!.palette.background['paper-elevation-1'],

        // Sort icon is decorative — header click handles sort, so strip IconButton chrome
        '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root': {
          padding: 0,
          '&:hover, &:focus, &:focus-visible': {
            backgroundColor: 'transparent',
          },
        },
        // Sort icon: shrink to theme "small" (16px) and optical-center vs mixed-case text
        '& .MuiDataGrid-sortIcon': {
          fontSize: '16px',
          transform: 'translateY(1px)',
        },

        // Pinned right column sits on top of the scrolling cells
        '& .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-filler--pinnedRight': {
          boxShadow: PINNED_EDGE_SHADOW,
        },
        '& .MuiDataGrid-cell': {
          borderRadius: '0 !important',
        },
        '& .MuiDataGrid-scrollbarFiller': {
          borderTop: 'none !important',
          borderBottom: 'none !important',
        },
        // Remove default border on filler
        '& .MuiDataGrid-filler': {
          '--rowBorderColor': 'transparent !important',
          borderRadius: '0 !important',
        },
        // Remove border on rows pinned right cells
        '& .MuiDataGrid-cell--withLeftBorder': {
          borderLeft: 'none',
        },
        // Remove border on header pinned right cells
        '& .MuiDataGrid-columnHeader--withLeftBorder': {
          borderLeft: 'none',
          boxShadow: PINNED_EDGE_SHADOW,
        },
        '& .MuiDataGrid-columnHeader': {
          borderRadius: '0 !important',
        },
        // Remove border on filler pinned right cells
        '& .MuiDataGrid-filler--pinnedRight': {
          borderLeft: 'none',
        },
      }),
      columnHeaders: ({ theme }: { theme: Theme }) => ({
        borderBottom: 'none',
        borderRadius: 0,
      }),
      columnHeader: ({ theme }: { theme: Theme }) => ({
        display: 'flex',
        alignItems: 'center',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        '&:focus, &:focus-within': {
          outline: 'none',
        },
        '&.MuiDataGrid-columnHeader--sortable:hover': {},
        '&.header-with-tooltip .MuiDataGrid-columnHeaderTitle': {
          ...theme.custom.helpText,
          lineHeight: '20px',
        },
      }),
      columnHeaderTitle: ({ theme }: { theme: Theme }) => ({
        fontWeight: 500,
        color: theme.vars!.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
      }),
      cell: ({ theme }: { theme: Theme }) => ({
        borderBottom: 'none',
        borderRight: 'none',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        // The row filler cell must collapse to 0 when columns overflow; padded, it adds 16px to every row and
        // lets the scroller run that far past the header row.
        '&.MuiDataGrid-cellEmpty': {
          paddingLeft: 0,
          paddingRight: 0,
        },
        '&:focus, &:focus-within': {
          outline: 'none',
        },
      }),
      row: ({ theme }: { theme: Theme }) => ({
        borderBottom: 'none',
      }),
      virtualScroller: ({ theme }: { theme: Theme }) => ({}),
      footerContainer: ({ theme }: { theme: Theme }) => ({
        borderTop: 'none',
        // Figma <TableFooter>: 40px icon buttons + 2px vertical padding. MUI's toolbar defaults to 52px.
        minHeight: 44,
        '& .MuiTablePagination-toolbar': { minHeight: 44 },
      }),
      columnSeparator: {
        display: 'none',
      },
      // Custom scrollbar
      scrollbar: ({ theme }: { theme: Theme }) => ({
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.vars!.palette.background['paper-elevation-1'],
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.vars!.palette.primary._states.focus,
          borderRadius: '4px',
          '&:hover': {
            // backgroundColor: theme.vars.palette.text.secondary,
          },
        },
      }),
    },
  },
};
